import { createRootRoute, createRoute, createRouter, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { authClient } from "./lib/auth/client";
import { RootLayout } from "./routes/__root";
import { AcceptInvitePage } from "./routes/accept-invite";
import { DashboardPage } from "./routes/dashboard/index";
import { CategoriesPage } from "./routes/dashboard/settings/categories";
import { MatrixUploadPage } from "./routes/dashboard/settings/matrix-upload";
import { MetricsPage } from "./routes/dashboard/settings/metrics";
import { ProfilesPage } from "./routes/dashboard/settings/profiles";
import { ProvidersPage } from "./routes/dashboard/settings/providers";
import { SettingsUsersPage } from "./routes/dashboard/settings/users";
import { TeamPage } from "./routes/dashboard/team";
import { LoginPage } from "./routes/login";
import { SignupPage } from "./routes/signup";

const rootRoute = createRootRoute({ component: RootLayout });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/login" });
  },
});

const authSearchSchema = z.object({
  next: z.string().optional(),
  email: z.string().optional(),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: authSearchSchema,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  validateSearch: authSearchSchema,
});

const dashboardSearchSchema = z.object({
  // "home" = the Atlas hero (start a campaign); "campaigns" = the campaigns
  // list + results. A `campaign` id implies the campaigns section.
  section: z.enum(["home", "campaigns"]).optional(),
  campaign: z.string().optional(),
  view: z.enum(["table", "map", "charts"]).optional(),
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
  validateSearch: dashboardSearchSchema,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) throw redirect({ to: "/login" });
  },
});

async function requireAdmin() {
  const session = await authClient.getSession();
  if (!session.data) throw redirect({ to: "/login" });
  if (session.data.user.role !== "admin") throw redirect({ to: "/dashboard" });
}

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/team",
  component: TeamPage,
  beforeLoad: async () => {
    const session = await authClient.getSession();
    if (!session.data) throw redirect({ to: "/login" });
  },
});

const acceptInviteSearchSchema = z.object({ token: z.string().optional() });

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/accept-invite",
  component: AcceptInvitePage,
  validateSearch: acceptInviteSearchSchema,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings",
  beforeLoad: async () => {
    await requireAdmin();
    throw redirect({ to: "/dashboard/settings/users" });
  },
});

const settingsProvidersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/providers",
  component: ProvidersPage,
  beforeLoad: requireAdmin,
});

const settingsUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/users",
  component: SettingsUsersPage,
  beforeLoad: requireAdmin,
});

const settingsMatrixUploadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/matrix-upload",
  component: MatrixUploadPage,
  beforeLoad: requireAdmin,
});

const settingsCategoriesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/categories",
  component: CategoriesPage,
  beforeLoad: requireAdmin,
});

const settingsMetricsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/metrics",
  component: MetricsPage,
  beforeLoad: requireAdmin,
});

const settingsProfilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard/settings/profiles",
  component: ProfilesPage,
  beforeLoad: requireAdmin,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  dashboardRoute,
  teamRoute,
  acceptInviteRoute,
  settingsRoute,
  settingsProvidersRoute,
  settingsUsersRoute,
  settingsMatrixUploadRoute,
  settingsCategoriesRoute,
  settingsMetricsRoute,
  settingsProfilesRoute,
]);

export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
